import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axiosInstance from "../../api/axiosInstance";

const PAGE_SIZE = 10;
const FORM_DRAFT_KEY = "city_form_draft";
const TOAST_DURATION = 4000;

const api = {
  list: (countryId = "", stateId = "", search = "", isActive = "") => {
    const p = new URLSearchParams();
    if (countryId) p.append("country_id", countryId);
    if (stateId)   p.append("state_id",   stateId);
    if (search)    p.append("q",           search);
    if (isActive !== "") p.append("is_active", isActive);
    return axiosInstance.get(`/api/superadmin/master/cities/?${p}`);
  },
  get: (id) =>
    axiosInstance.get(`/api/superadmin/master/cities/${id}/`),
  create: (body) =>
    axiosInstance.post(`/api/superadmin/master/cities/`, body),
  update: (id, body) =>
    axiosInstance.patch(`/api/superadmin/master/cities/${id}/`, body),
  remove: (id) =>
    axiosInstance.delete(`/api/superadmin/master/cities/${id}/`),
  countries: async (search = "") => {
    const p = new URLSearchParams();
    if (search) p.append("q", search);
    try {
      const res = await axiosInstance.get(`/api/superadmin/master/countries/?${p}`);
      return res.data?.results || [];
    } catch {
      return [];
    }
  },
  states: async (countryId = "", search = "") => {
    const p = new URLSearchParams();
    if (countryId) p.append("country_id", countryId);
    if (search)    p.append("q",           search);
    try {
      const res = await axiosInstance.get(`/api/superadmin/master/states/?${p}`);
      return res.data?.results || [];
    } catch {
      return [];
    }
  },
};

const formDraft = {
  save:  (data) => { try { sessionStorage.setItem(FORM_DRAFT_KEY, JSON.stringify(data)); } catch { /**/ } },
  load:  ()     => { try { const r = sessionStorage.getItem(FORM_DRAFT_KEY); return r ? JSON.parse(r) : null; } catch { return null; } },
  clear: ()     => { try { sessionStorage.removeItem(FORM_DRAFT_KEY); } catch { /**/ } },
};

const PlusIcon = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>);
const EditIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>);
const SearchIcon = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>);
const CloseIcon = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>);
const ChevronLeftIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><polyline points="15 18 9 12 15 6" /></svg>);
const ChevronRightIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><polyline points="9 18 15 12 9 6" /></svg>);
const ChevronDownIcon = () => (<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>);
const RestoreIcon = () => (<svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M11 6.5A4.5 4.5 0 112 4.5M2 2v2.5h2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>);

// ── NEW: icons for confirm dialogs ────────────────────────────────────────────
const WarningIcon = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
    <circle cx="24" cy="24" r="24" fill="#FEF3C7" />
    <path d="M24 13L11 37h26L24 13z" stroke="#D97706" strokeWidth="2" strokeLinejoin="round" fill="#FDE68A" />
    <path d="M24 22v7M24 31.5v1" stroke="#D97706" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);
const CheckCircleIcon = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
    <circle cx="24" cy="24" r="24" fill="#DCFCE7" />
    <circle cx="24" cy="24" r="14" stroke="#16a34a" strokeWidth="2" fill="#BBF7D0" />
    <path d="M17 24l5 5 9-9" stroke="#16a34a" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

let _toastSetter = null;
const toast = {
  _push: (item) => {
    const id = Date.now() + Math.random();
    _toastSetter?.((p) => [...p, { id, ...item }]);
    return id;
  },
  success: (title, msg)  => toast._push({ type: "success", title, msg }),
  error:   (title, msg)  => toast._push({ type: "error",   title, msg }),
  info:    (title, msg)  => toast._push({ type: "info",    title, msg }),
  loading: (title)       => toast._push({ type: "loading", title, persistent: true }),
  dismiss: (id)          => _toastSetter?.((p) => p.filter((t) => t.id !== id)),
};

const TOAST_META = {
  success: { bar: "#16a34a", icon: "✓" },
  error:   { bar: "#dc2626", icon: "✕" },
  info:    { bar: "#1d4ed8", icon: "i" },
  loading: { bar: "#94a3b8", icon: null },
};

const ToastItem = ({ t, onDismiss }) => {
  const meta = TOAST_META[t.type];
  const [progress, setProgress] = useState(100);
  const [exiting,  setExiting]  = useState(false);
  const rafRef  = useRef(null);
  const startTs = useRef(Date.now());

  const dismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => onDismiss(t.id), 280);
  }, [t.id, onDismiss]);

  useEffect(() => {
    if (t.persistent) return;
    const tick = () => {
      const pct = Math.max(0, 100 - ((Date.now() - startTs.current) / TOAST_DURATION) * 100);
      setProgress(pct);
      if (pct > 0) rafRef.current = requestAnimationFrame(tick);
      else dismiss();
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [t.persistent, dismiss]);

  return (
    <div style={{
      position: "relative", overflow: "hidden", background: "#fff",
      borderRadius: 10, borderLeft: `4px solid ${meta.bar}`,
      boxShadow: "0 4px 20px rgba(0,0,0,0.10), 0 0 0 1px rgba(0,0,0,0.06)",
      minWidth: 300, maxWidth: 360,
      animation: exiting ? "toastOut 0.28s ease forwards" : "toastIn 0.28s cubic-bezier(0.34,1.56,0.64,1) forwards",
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "13px 14px 14px" }}>
        <div style={{ flexShrink: 0, width: 24, height: 24, borderRadius: "50%", background: `${meta.bar}18`, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>
          {t.type === "loading" ? (
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ animation: "spin 0.85s linear infinite" }}>
              <circle cx="6.5" cy="6.5" r="5" stroke={meta.bar} strokeWidth="2" strokeDasharray="8 20" strokeLinecap="round" />
            </svg>
          ) : (
            <span style={{ fontSize: 11, fontWeight: 800, color: meta.bar, lineHeight: 1 }}>{meta.icon}</span>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 13.5, fontWeight: 650, color: "#0f172a", lineHeight: 1.35 }}>{t.title}</p>
          {t.msg && <p style={{ margin: "3px 0 0", fontSize: 12.5, color: "#64748b", lineHeight: 1.5 }}>{t.msg}</p>}
        </div>
        <button onClick={dismiss}
          style={{ background: "none", border: "none", cursor: "pointer", color: "#cbd5e1", padding: "2px", flexShrink: 0, display: "flex", borderRadius: 4, transition: "color 0.15s", marginTop: 1 }}
          onMouseEnter={(e) => e.currentTarget.style.color = "#94a3b8"}
          onMouseLeave={(e) => e.currentTarget.style.color = "#cbd5e1"}>
          <CloseIcon />
        </button>
      </div>
      {!t.persistent && (
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: "#f1f5f9" }}>
          <div style={{ height: "100%", width: `${progress}%`, background: meta.bar, transition: "width 0.1s linear", borderRadius: "0 2px 0 0" }} />
        </div>
      )}
    </div>
  );
};

const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);
  _toastSetter = setToasts;
  const dismiss = useCallback((id) => setToasts((p) => p.filter((t) => t.id !== id)), []);
  if (!toasts.length) return null;
  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999, display: "flex", flexDirection: "column", gap: 10, alignItems: "flex-end" }}>
      {toasts.map((t) => <ToastItem key={t.id} t={t} onDismiss={dismiss} />)}
    </div>
  );
};

const DraftBanner = ({ onRestore, onDiscard }) => (
  <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 14px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 16 }}>📝</span>
      <div>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#92400e" }}>Unsaved draft found</p>
        <p style={{ margin: 0, fontSize: 12, color: "#b45309" }}>You have a previously unsaved form. Would you like to restore it?</p>
      </div>
    </div>
    <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
      <button onClick={onDiscard} style={{ padding: "5px 12px", borderRadius: 8, fontSize: 12.5, fontWeight: 570, cursor: "pointer", background: "#f1f5f9", color: "#475569", border: "1.5px solid #e2e8f0" }}>Discard</button>
      <button onClick={onRestore}
        style={{ padding: "5px 12px", borderRadius: 8, fontSize: 12.5, fontWeight: 570, cursor: "pointer", background: "#d97706", color: "#fff", border: "none", display: "inline-flex", alignItems: "center", gap: 6 }}
        onMouseEnter={(e) => e.currentTarget.style.background = "#b45309"}
        onMouseLeave={(e) => e.currentTarget.style.background = "#d97706"}>
        <RestoreIcon /> Restore
      </button>
    </div>
  </div>
);

const SearchableDropdown = ({
  value = "", displayText = "", placeholder = "Select…",
  onSelect, onClear, fetchFn,
  getOptionId = (o) => String(o.id),
  getOptionLabel = (o) => o.name,
  renderOption, disabled = false,
  searchPlaceholder = "Search…",
  triggerStyle = {},
}) => {
  const [open,     setOpen]     = useState(false);
  const [query,    setQuery]    = useState("");
  const [options,  setOptions]  = useState([]);
  const [fetching, setFetching] = useState(false);
  const wrapperRef = useRef(null);
  const timerRef   = useRef(null);
  const fetchRef   = useRef(fetchFn);
  useEffect(() => { fetchRef.current = fetchFn; }, [fetchFn]);

  useEffect(() => {
    const handler = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setFetching(true);
      try { const r = await fetchRef.current(query); setOptions(Array.isArray(r) ? r : []); }
      catch { setOptions([]); }
      finally { setFetching(false); }
    }, 280);
    return () => clearTimeout(timerRef.current);
  }, [query, open]);

  const handleSelect = (opt) => { onSelect(getOptionId(opt), getOptionLabel(opt), opt); setOpen(false); setQuery(""); };
  const handleClear  = (e)   => { e.stopPropagation(); onClear(); setQuery(""); };

  const base = { width: "100%", padding: "9px 10px 9px 12px", borderRadius: 8, fontSize: 13.5, outline: "none", border: "1.5px solid #e2e8f0", background: "#f8fafc", color: "#0f172a", transition: "border 0.15s", boxSizing: "border-box", fontFamily: "inherit" };

  return (
    <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
      <div onClick={() => !disabled && setOpen((o) => !o)}
        style={{ ...base, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: disabled ? "not-allowed" : "pointer", userSelect: "none", opacity: disabled ? 0.5 : 1, borderColor: open ? "#3b82f6" : "#e2e8f0", background: open ? "#fff" : "#f8fafc", ...triggerStyle }}>
        <span style={{ fontSize: 13.5, color: displayText ? "#0f172a" : "#94a3b8", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {displayText || placeholder}
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0, marginLeft: 6 }}>
          {value && !disabled && (
            <span onClick={handleClear} title="Clear"
              style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 16, height: 16, borderRadius: "50%", background: "#cbd5e1", color: "#fff", cursor: "pointer", fontSize: 10 }}>
              ✕
            </span>
          )}
          <span style={{ color: "#94a3b8", display: "flex", transform: open ? "rotate(180deg)" : "none", transition: "transform 0.18s" }}>
            <ChevronDownIcon />
          </span>
        </span>
      </div>

      {open && !disabled && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.10)", zIndex: 600, overflow: "hidden", minWidth: 200, animation: "cmFadeUp 0.15s ease" }}>
          <div style={{ padding: "8px 10px", borderBottom: "1px solid #f1f5f9" }}>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none", display: "flex" }}><SearchIcon /></span>
              <input autoFocus type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={searchPlaceholder}
                style={{ width: "100%", paddingLeft: 30, padding: "8px 10px 8px 30px", border: "1.5px solid #e2e8f0", borderRadius: 7, fontSize: 13, background: "#f8fafc", color: "#0f172a", outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
                onFocus={(e) => { e.target.style.borderColor = "#3b82f6"; e.target.style.background = "#fff"; }}
                onBlur={(e)  => { e.target.style.borderColor = "#e2e8f0"; e.target.style.background = "#f8fafc"; }} />
            </div>
          </div>
          <div style={{ maxHeight: 230, overflowY: "auto" }}>
            {fetching ? (
              <div style={{ padding: 16, fontSize: 13, color: "#94a3b8", textAlign: "center" }}>Loading…</div>
            ) : options.length === 0 ? (
              <div style={{ padding: 16, fontSize: 13, color: "#94a3b8", textAlign: "center" }}>No results found</div>
            ) : (
              options.map((opt) => {
                const id = getOptionId(opt);
                const isSelected = id === value;
                return (
                  <div key={id} onMouseDown={(e) => { e.preventDefault(); handleSelect(opt); }}
                    style={{ padding: "9px 14px", fontSize: 13.5, cursor: "pointer", color: isSelected ? "#2563eb" : "#1e293b", background: isSelected ? "#eff6ff" : "transparent", fontWeight: isSelected ? 600 : 400, display: "flex", alignItems: "center", gap: 8, transition: "background 0.1s" }}
                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = "#f8fafc"; }}
                    onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}>
                    {renderOption ? renderOption(opt, isSelected) : getOptionLabel(opt)}
                    {isSelected && <span style={{ marginLeft: "auto", color: "#2563eb", fontSize: 12 }}>✓</span>}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CountryFilterDropdown = ({ value, onChange }) => {
  const [displayText, setDisplayText] = useState("");
  const fetchFn = useCallback((q) => api.countries(q), []);
  return (
    <div style={{ flex: "0 1 190px" }}>
      <SearchableDropdown
        value={value} displayText={displayText} placeholder="All Countries"
        onSelect={(id, label) => { onChange(id); setDisplayText(label); }}
        onClear={() => { onChange(""); setDisplayText(""); }}
        fetchFn={fetchFn}
        searchPlaceholder="Search country…"
        renderOption={(opt, isSelected) => (
          <>
            {opt.iso2 && <span style={{ fontSize: 11, fontWeight: 660, background: "#e0f2fe", color: "#0369a1", padding: "1px 5px", borderRadius: 4, letterSpacing: "0.04em", flexShrink: 0 }}>{opt.iso2}</span>}
            <span style={{ flex: 1 }}>{opt.name}</span>
            {isSelected && <span style={{ color: "#2563eb", fontSize: 12 }}>✓</span>}
          </>
        )}
      />
    </div>
  );
};

const StateFilterDropdown = ({ value, onChange, countryId }) => {
  const [displayText, setDisplayText] = useState("");
  useEffect(() => { setDisplayText(""); onChange("");  }, [countryId, onChange]);
  const fetchFn = useCallback((q) => api.states(countryId, q), [countryId]);
  return (
    <div style={{ flex: "0 1 210px" }}>
      <SearchableDropdown
        value={value} displayText={displayText}
        placeholder={countryId ? "All States" : "Select Country First"}
        onSelect={(id, label) => { onChange(id); setDisplayText(label); }}
        onClear={() => { onChange(""); setDisplayText(""); }}
        fetchFn={fetchFn}
        searchPlaceholder="Search state…"
        disabled={!countryId}
      />
    </div>
  );
};

const SkeletonRow = () => (
  <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
    {[40, 160, 120, 100, 90, 90, 70, 130, 80].map((w, i) => (
      <td key={i} style={{ padding: "14px 16px" }}>
        <div style={{ height: 13, width: w, maxWidth: "100%", borderRadius: 6, background: "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)", backgroundSize: "200% 100%", animation: "cmShimmer 1.4s infinite" }} />
      </td>
    ))}
  </tr>
);
const TableSkeleton = () => <>{Array.from({ length: PAGE_SIZE }).map((_, i) => <SkeletonRow key={i} />)}</>;

const Pagination = ({ currentPage, totalPages, totalCount, onPageChange }) => {
  if (totalPages <= 1) return null;
  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem   = Math.min(currentPage * PAGE_SIZE, totalCount);
  const getPages  = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [1];
    if (currentPage > 3) pages.push("...");
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push("...");
    pages.push(totalPages);
    return pages;
  };
  const btnBase = { display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 34, height: 34, padding: "0 6px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer", background: "#fff", color: "#374151", transition: "all 0.15s", lineHeight: 1 };
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderTop: "1px solid #f1f5f9", background: "#f8fafc", flexWrap: "wrap", gap: 10 }}>
      <span style={{ fontSize: 13, color: "#64748b" }}>Showing <strong style={{ color: "#374151" }}>{startItem}–{endItem}</strong> of <strong style={{ color: "#374151" }}>{totalCount}</strong> results</span>
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} style={{ ...btnBase, opacity: currentPage === 1 ? 0.4 : 1, cursor: currentPage === 1 ? "not-allowed" : "pointer" }}><ChevronLeftIcon /></button>
        {getPages().map((p, i) =>
          p === "..." ? <span key={`e${i}`} style={{ padding: "0 4px", color: "#94a3b8", fontSize: 13, lineHeight: "34px" }}>…</span>
          : <button key={p} onClick={() => onPageChange(p)} className={p === currentPage ? "" : "cm-page-btn"}
              style={{ ...btnBase, ...(p === currentPage ? { background: "#3b82f6", color: "#fff", border: "1px solid #3b82f6", fontWeight: 600 } : {}) }}>{p}</button>
        )}
        <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} style={{ ...btnBase, opacity: currentPage === totalPages ? 0.4 : 1, cursor: currentPage === totalPages ? "not-allowed" : "pointer" }}><ChevronRightIcon /></button>
      </div>
    </div>
  );
};

const Toggle = ({ value, onChange }) => (
  <div onClick={() => onChange(!value)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}>
    <div style={{ width: 42, height: 24, borderRadius: 12, background: value ? "#22c55e" : "#cbd5e1", position: "relative", transition: "background 0.2s" }}>
      <div style={{ position: "absolute", top: 4, left: value ? 22 : 4, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
    </div>
    <span style={{ fontSize: 13, fontWeight: 600, color: value ? "#16a34a" : "#94a3b8" }}>{value ? "Active" : "Inactive"}</span>
  </div>
);

const Badge = ({ active }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, ...(active ? { background: "#dcfce7", color: "#16a34a" } : { background: "#fee2e2", color: "#dc2626" }) }}>
    <span style={{ width: 6, height: 6, borderRadius: "50%", background: active ? "#22c55e" : "#f43f5e", display: "inline-block" }} />
    {active ? "Active" : "Inactive"}
  </span>
);

const Modal = ({ title, onClose, children }) => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; document.removeEventListener("keydown", onKey); };
  }, [onClose]);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}
      onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 560, boxShadow: "0 24px 64px rgba(0,0,0,0.18)", animation: "cmFadeUp 0.2s ease" }}
        onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", borderBottom: "1px solid #f1f5f9" }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#0f172a" }}>{title}</h2>
        </div>
        <div style={{ padding: "20px 24px 24px", maxHeight: "80vh", overflowY: "auto" }}>{children}</div>
      </div>
    </div>
  );
};

const EMPTY_FORM = { country_id: "", country_name: "", state_id: "", state_name: "", name: "", latitude: "", longitude: "", is_active: true };

const CityForm = ({ initial, onSubmit, onCancel, loading }) => {
  const isEdit     = !!initial;
  const savedDraft = !isEdit ? formDraft.load() : null;

  const [showBanner, setShowBanner] = useState(!isEdit && !!savedDraft);
  const [form,    setForm]    = useState(isEdit ? {
    country_id:   initial.country_id   ?? "",
    country_name: initial.country_name ?? "",
    state_id:     initial.state_id     ?? "",
    state_name:   initial.state_name   ?? "",
    name:         initial.name         ?? "",
    latitude:     initial.latitude     ?? "",
    longitude:    initial.longitude    ?? "",
    is_active:    initial.is_active    ?? true,
  } : EMPTY_FORM);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEdit || showBanner) return;
    formDraft.save(form);
  }, [form, isEdit, showBanner]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: "" })); };

  const handleRestore = () => { if (savedDraft) setForm(savedDraft); setShowBanner(false); };
  const handleDiscard = () => { formDraft.clear(); setShowBanner(false); setForm(EMPTY_FORM); };
  const handleCancel  = () => { if (!isEdit) formDraft.clear(); onCancel(); };

  const validate = () => {
    const e = {};
    if (!form.state_id)   e.state_id  = "Please select a state.";
    if (!form.name.trim()) e.name     = "City name is required.";
    if (form.latitude === "" || form.latitude === null || form.latitude === undefined) e.latitude  = "Latitude is required.";
    if (form.longitude === "" || form.longitude === null || form.longitude === undefined) e.longitude = "Longitude is required.";
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    onSubmit({
      state_id:  Number(form.state_id),
      name:      form.name.trim(),
      latitude:  parseFloat(form.latitude),
      longitude: parseFloat(form.longitude),
      is_active: form.is_active,
    }, setErrors);
  };

  const fetchCountries = useCallback((q) => api.countries(q), []);
  const fetchStates    = useCallback((q) => api.states(form.country_id, q), [form.country_id]);

  const inputStyle = (key) => ({
    width: "100%", padding: "9px 12px", border: `1.5px solid ${errors[key] ? "#f43f5e" : "#e2e8f0"}`,
    borderRadius: 8, fontSize: 14, color: "#1e293b", outline: "none",
    background: errors[key] ? "#fff1f2" : "#f8fafc", boxSizing: "border-box",
    fontFamily: "inherit", transition: "border 0.15s",
  });
  const labelStyle = { fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 };

  return (
    <div>
      {showBanner && <DraftBanner onRestore={handleRestore} onDiscard={handleDiscard} />}

      {errors.__global && (
        <div style={{ background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}>
          <p style={{ margin: 0, fontSize: 13, color: "#dc2626", fontWeight: 550 }}>⚠ {errors.__global}</p>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <label style={labelStyle}>Country <span style={{ color: "#94a3b8", fontWeight: 400 }}>(optional)</span></label>
          <SearchableDropdown
            value={form.country_id} displayText={form.country_name}
            placeholder="Select country…"
            onSelect={(id, label) => { set("country_id", id); set("country_name", label); set("state_id", ""); set("state_name", ""); }}
            onClear={() => { set("country_id", ""); set("country_name", ""); set("state_id", ""); set("state_name", ""); }}
            fetchFn={fetchCountries}
            searchPlaceholder="Search country…"
            renderOption={(opt, isSelected) => (
              <>{opt.iso2 && <span style={{ fontSize: 11, fontWeight: 660, background: "#e0f2fe", color: "#0369a1", padding: "1px 5px", borderRadius: 4, letterSpacing: "0.04em", flexShrink: 0 }}>{opt.iso2}</span>}<span style={{ flex: 1 }}>{opt.name}</span>{isSelected && <span style={{ color: "#2563eb", fontSize: 12 }}>✓</span>}</>
            )}
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <label style={{ ...labelStyle, color: errors.state_id ? "#f43f5e" : "#374151" }}>State <span style={{ color: "#f43f5e" }}>*</span></label>
          <SearchableDropdown
            value={form.state_id ? String(form.state_id) : ""}
            displayText={form.state_name}
            placeholder={form.country_id ? "Search & select state…" : "Search state (all)…"}
            onSelect={(id, label) => { set("state_id", id); set("state_name", label); }}
            onClear={() => { set("state_id", ""); set("state_name", ""); }}
            fetchFn={fetchStates}
            searchPlaceholder={form.country_id ? "Search state…" : "Search state (all countries)…"}
            triggerStyle={errors.state_id ? { borderColor: "#f43f5e", background: "#fff1f2" } : {}}
          />
          {errors.state_id && <span style={{ fontSize: 12, color: "#f43f5e" }}>⚠ {errors.state_id}</span>}
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={{ ...labelStyle, color: errors.name ? "#f43f5e" : "#374151" }}>City Name <span style={{ color: "#f43f5e" }}>*</span></label>
        <input type="text" style={inputStyle("name")} placeholder="e.g. Ahmedabad" value={form.name}
          onChange={(e) => set("name", e.target.value)}
          onFocus={(e) => { e.target.style.borderColor = "#3b82f6"; e.target.style.background = "#fff"; }}
          onBlur={(e)  => { e.target.style.borderColor = errors.name ? "#f43f5e" : "#e2e8f0"; e.target.style.background = errors.name ? "#fff1f2" : "#f8fafc"; }} />
        {errors.name && <span style={{ fontSize: 12, color: "#f43f5e" }}>⚠ {errors.name}</span>}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
        {[{ label: "Latitude", key: "latitude", ph: "e.g. 23.022505" }, { label: "Longitude", key: "longitude", ph: "e.g. 72.571365" }].map(({ label, key, ph }) => (
          <div key={key} style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <label style={{ ...labelStyle, color: errors[key] ? "#f43f5e" : "#374151" }}>{label} <span style={{ color: "#f43f5e" }}>*</span></label>
            <input type="number" step="any" style={inputStyle(key)} placeholder={ph} value={form[key]}
              onChange={(e) => set(key, e.target.value)}
              onFocus={(e) => { e.target.style.borderColor = "#3b82f6"; e.target.style.background = "#fff"; }}
              onBlur={(e)  => { e.target.style.borderColor = errors[key] ? "#f43f5e" : "#e2e8f0"; e.target.style.background = errors[key] ? "#fff1f2" : "#f8fafc"; }} />
            {errors[key] && <span style={{ fontSize: 12, color: "#f43f5e" }}>⚠ {errors[key]}</span>}
          </div>
        ))}
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 22, padding: "12px 14px", background: "#f8fafc", borderRadius: 8, border: "1.5px solid #e2e8f0" }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>Status</label>
        <Toggle value={form.is_active} onChange={(v) => set("is_active", v)} />
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingTop: 16, borderTop: "1px solid #f1f5f9" }}>
        <button onClick={handleCancel} disabled={loading}
          style={{ padding: "9px 20px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", color: "#64748b", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: loading ? 0.6 : 1 }}>
          Cancel
        </button>
        <button onClick={handleSubmit} disabled={loading}
          style={{ padding: "9px 22px", border: "none", borderRadius: 8, background: "#3b82f6", color: "#fff", fontSize: 14, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1 }}>
          {loading ? "Saving…" : initial ? "Update City" : "Add City"}
        </button>
      </div>
    </div>
  );
};

// ── Deactivate confirm (unchanged) ────────────────────────────────────────────
const DeactivateConfirm = ({ city, onConfirm, onCancel, loading }) => (
  <div style={{ textAlign: "center", padding: "4px 0 0" }}>
    <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}><WarningIcon /></div>
    <p style={{ fontSize: 16, fontWeight: 600, color: "#0f172a", margin: "0 0 6px" }}>
      Deactivate <span style={{ color: "#f43f5e" }}>"{city.name}"</span>?
    </p>
    <p style={{ fontSize: 13, color: "#94a3b8", margin: "0 0 24px" }}>This will mark the city as inactive. You can reactivate it later.</p>
    <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
      <button onClick={onCancel} disabled={loading} style={{ padding: "9px 20px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", color: "#64748b", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: loading ? 0.6 : 1 }}>Cancel</button>
      <button onClick={onConfirm} disabled={loading} style={{ padding: "9px 22px", border: "none", borderRadius: 8, background: "#f43f5e", color: "#fff", fontSize: 14, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1 }}>
        {loading ? "Deactivating…" : "Yes, Deactivate"}
      </button>
    </div>
  </div>
);

// ── CHANGE 1: Activate confirm (mirrors DeactivateConfirm, green theme) ───────
const ActivateConfirm = ({ city, onConfirm, onCancel, loading }) => (
  <div style={{ textAlign: "center", padding: "4px 0 0" }}>
    <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}><CheckCircleIcon /></div>
    <p style={{ fontSize: 16, fontWeight: 600, color: "#0f172a", margin: "0 0 6px" }}>
      Activate <span style={{ color: "#16a34a" }}>"{city.name}"</span>?
    </p>
    <p style={{ fontSize: 13, color: "#94a3b8", margin: "0 0 24px" }}>This will mark the city as active and make it available for use.</p>
    <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
      <button onClick={onCancel} disabled={loading} style={{ padding: "9px 20px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", color: "#64748b", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: loading ? 0.6 : 1 }}>Cancel</button>
      <button onClick={onConfirm} disabled={loading} style={{ padding: "9px 22px", border: "none", borderRadius: 8, background: "#16a34a", color: "#fff", fontSize: 14, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1 }}>
        {loading ? "Activating…" : "Yes, Activate"}
      </button>
    </div>
  </div>
);

// ── CHANGE 2: Stats bar (computed from allCities — no extra API calls) ─────────
const StatsBar = ({ cities, loading }) => {
  const total    = cities.length;
  const active   = cities.filter((c) => c.is_active).length;
  const inactive = total - active;

  const Card = ({ label, count, color, bg, border }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 18px", borderRadius: 10, background: bg, border: `1px solid ${border}`, minWidth: 110 }}>
      <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a", lineHeight: 1.1 }}>
          {loading
            ? <div style={{ width: 28, height: 16, borderRadius: 4, background: "#e2e8f0", animation: "cmShimmer 1.4s infinite" }} />
            : count}
        </div>
        <div style={{ fontSize: 11.5, color: "#64748b", fontWeight: 550, marginTop: 2, letterSpacing: "0.02em" }}>{label}</div>
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
      <Card label="Total Cities" count={total}    color="#6366f1" bg="#f5f3ff" border="#e0e7ff" />
      <Card label="Active"       count={active}   color="#16a34a" bg="#f0fdf4" border="#bbf7d0" />
      <Card label="Inactive"     count={inactive} color="#dc2626" bg="#fef2f2" border="#fecaca" />
    </div>
  );
};

const CityMaster = () => {
  const [allCities,     setAllCities]     = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [deletingId,    setDeletingId]    = useState(null);
  const [activatingId,  setActivatingId]  = useState(null);
  const [currentPage,   setCurrentPage]   = useState(1);
  const [search,        setSearch]        = useState("");
  const [filterCountry, setFilterCountry] = useState("");
  const [filterState,   setFilterState]   = useState("");
  const [filterActive,  setFilterActive]  = useState("");
  const [showAdd,       setShowAdd]       = useState(false);
  const [editCity,      setEditCity]      = useState(null);
  const [deleteCity,    setDeleteCity]    = useState(null);
  // ── CHANGE 1: state for activate confirm ──
  const [activateCity,  setActivateCity]  = useState(null);

  const fetchCities = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.list(filterCountry, filterState, search, filterActive);

      if (data?.status === 401) { toast.error("Session Expired", "Please log in again."); setLoading(false); return; }
      if (data?.status === 403) { toast.error("Access Denied", "You don't have permission to view cities."); setLoading(false); return; }
      if (data?.status >= 400) throw new Error("Load failed");

      setAllCities([...data?.results].sort((a, b) => (b.is_active ? 1 : 0) - (a.is_active ? 1 : 0)));
    } catch (err) {
      if (err?.name === "TypeError") {
        toast.error("Network Error", "Unable to reach the server. Check your connection.");
      } else {
        toast.error("Failed to Load", "Unable to fetch cities. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [search, filterCountry, filterState, filterActive]);

  useEffect(() => { setCurrentPage(1); }, [search, filterCountry, filterState, filterActive]);
  useEffect(() => { const t = setTimeout(fetchCities, 400); return () => clearTimeout(t); }, [fetchCities]);

  const totalCount  = allCities.length;
  const totalPages  = Math.ceil(totalCount / PAGE_SIZE);
  const pagedCities = useMemo(() => allCities.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE), [allCities, currentPage]);

  const applyFieldErrors = (data, setErrors) => {
    if (!data || typeof data !== "object" || data?.detail) return false;
    const mapped = {};
    let hasFields = false;
    Object.keys(data).forEach((k) => {
      const v = data[k];
      if (k === "non_field_errors") { mapped.__global = Array.isArray(v) ? v[0] : String(v); }
      else { mapped[k] = Array.isArray(v) ? v[0] : String(v); }
      hasFields = true;
    });
    if (hasFields) { setErrors(mapped); return true; }
    return false;
  };

  const handleAdd = async (body, setErrors) => {
    setActionLoading(true);
    const tid = toast.loading("Adding city…");
    try {
      const { data } = await api.create(body);
      toast.dismiss(tid);

      if (data?.status >= 400) {
        if (data?.data && applyFieldErrors(data?.data, setErrors)) {
          if (Object.keys(data?.data).length > 1 || data?.data?.non_field_errors) {
            toast.error("Validation Error", "Please fix the highlighted fields.");
          }
          return;
        }
        const msg = data?.data?.detail || `Failed to add city (${data?.status}). Please try again.`;
        toast.error("Create Failed", msg);
        return;
      }

      const created = data?.data ?? data;
      toast.success("City Added!", `"${created?.name || body.name}" has been added successfully.`);
      formDraft.clear();
      setShowAdd(false);
      fetchCities();
    } catch (err) {
      toast.dismiss(tid);
      toast.error("Network Error", err?.name === "TypeError" ? "Unable to reach the server." : "An unexpected error occurred.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdate = async (body, setErrors) => {
    setActionLoading(true);
    const tid = toast.loading("Updating city…");
    try {
      const { data } = await api.update(editCity.id, body);
      toast.dismiss(tid);

      if (data?.status >= 400) {
        if (data?.data && applyFieldErrors(data?.data, setErrors)) return;
        const msg = data?.data?.detail || `Update failed (${data?.status}). Please try again.`;
        toast.error("Update Failed", msg);
        return;
      }

      const updated = data?.data?.data ?? data?.data ?? data;
      toast.success("City Updated!", `"${updated?.name || editCity.name}" has been updated successfully.`);
      setEditCity(null);
      fetchCities();
    } catch (err) {
      toast.dismiss(tid);
      toast.error("Network Error", err?.name === "TypeError" ? "Unable to reach the server." : "An unexpected error occurred.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteCity) return;
    setActionLoading(true);
    setDeletingId(deleteCity.id);
    const tid = toast.loading(`Deactivating "${deleteCity.name}"…`);
    try {
      const { data } = await api.remove(deleteCity.id);
      toast.dismiss(tid);

      if (data?.status >= 400) {
        const msg = data?.data?.detail || `Deactivation failed (${data?.status}). Please try again.`;
        toast.error("Deactivate Failed", msg);
        return;
      }

      toast.success("Deactivated!", data?.data?.message || `"${deleteCity.name}" has been deactivated successfully.`);
      setDeleteCity(null);
      fetchCities();
    } catch (err) {
      toast.dismiss(tid);
      toast.error("Network Error", "Could not complete deactivation. Please try again.");
    } finally {
      setActionLoading(false);
      setDeletingId(null);
    }
  };

  // ── CHANGE 1: handleActivateConfirm reads from activateCity state ──────────
  const handleActivateConfirm = async () => {
    if (!activateCity) return;
    setActivatingId(activateCity.id);
    const tid = toast.loading(`Activating "${activateCity.name}"…`);
    try {
      const { data } = await api.update(activateCity.id, { is_active: true });
      toast.dismiss(tid);

      if (data?.status >= 400) {
        const msg = data?.data?.detail || `Activation failed (${data?.status}). Please try again.`;
        toast.error("Activate Failed", msg);
        return;
      }

      toast.success("Activated!", `"${activateCity.name}" is now active.`);
      setActivateCity(null);
      fetchCities();
    } catch (err) {
      toast.dismiss(tid);
      toast.error("Network Error", "Could not activate. Please try again.");
    } finally {
      setActivatingId(null);
    }
  };

  const openEdit = async (city) => {
    try {
      const { data } = await api.get(city.id);
      const src  = data?.id ? data : city;
      setEditCity({
        id:           src.id,
        state_id:     src.state     || src.state_id     || city.state     || "",
        state_name:   src.state_name                    || city.state_name || "",
        country_id:   src.country   || src.country_id   || city.country_id || "",
        country_name: src.country_name                  || city.country_name || "",
        name:         src.name      ?? "",
        latitude:     src.latitude  ?? "",
        longitude:    src.longitude ?? "",
        is_active:    src.is_active ?? true,
      });
    } catch {
      setEditCity({
        id:           city.id,
        state_id:     city.state    || city.state_id    || "",
        state_name:   city.state_name                   || "",
        country_id:   city.country  || city.country_id  || "",
        country_name: city.country_name                 || "",
        name:         city.name     ?? "",
        latitude:     city.latitude ?? "",
        longitude:    city.longitude ?? "",
        is_active:    city.is_active ?? true,
      });
    }
  };

  const hasFilters = search || filterCountry || filterState || filterActive;
  const inputBase  = { padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 14, background: "#f8fafc", color: "#1e293b", outline: "none", fontFamily: "inherit" };
  const COLS = ["Sr No.", "City Name", "State", "Country", "Latitude", "Longitude", "Status", "Created At", "Actions"];

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, sans-serif", padding: 24, background: "#f8fafc", minHeight: "100vh", color: "#1e293b" }}>
      <style>{`
        @keyframes cmShimmer { 0%   { background-position:  200% 0; } 100% { background-position: -200% 0; } }
        @keyframes cmFadeUp  { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes toastIn   { from { opacity: 0; transform: translateX(24px) scale(0.97); } to { opacity: 1; transform: translateX(0) scale(1); } }
        @keyframes toastOut  { from { opacity: 1; transform: translateX(0) scale(1); } to { opacity: 0; transform: translateX(28px) scale(0.95); } }
        @keyframes spin      { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .cm-row:hover       { background: #f8fafc !important; }
        .cm-edit-btn:hover  { background: #dbeafe !important; }
        .cm-del-btn:hover   { background: #ffe4e6 !important; }
        .cm-act-btn:hover   { background: #bbf7d0 !important; }
        .cm-add-btn:hover   { background: #2563eb !important; }
        .cm-page-btn:hover  { background: #f1f5f9 !important; border-color: #cbd5e1 !important; }
        * { box-sizing: border-box; }
      `}</style>

      <ToastContainer />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#0f172a" }}>City Master</h1>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748b" }}>Manage cities across states and countries master data</p>
        </div>
        <button className="cm-add-btn" onClick={() => setShowAdd(true)}
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 18px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer", transition: "background 0.15s" }}>
          <PlusIcon /> Add City
        </button>
      </div>

      {/* ── CHANGE 2: Stats bar above search filters ── */}
      <StatsBar cities={allCities} loading={loading} />

      <div style={{ background: "#fff", borderRadius: 12, padding: "14px 18px", border: "1px solid #e2e8f0", marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
        <div style={{ position: "relative", flex: "1 1 200px" }}>
          <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", display: "flex", pointerEvents: "none" }}><SearchIcon /></span>
          <input style={{ ...inputBase, width: "100%", paddingLeft: 34, boxSizing: "border-box" }} placeholder="Search cities…" value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={(e) => e.target.style.borderColor = "#3b82f6"}
            onBlur={(e)  => e.target.style.borderColor = "#e2e8f0"} />
        </div>

        <CountryFilterDropdown value={filterCountry} onChange={(v) => { setFilterCountry(v); setFilterState(""); }} />
        <StateFilterDropdown value={filterState} onChange={setFilterState} countryId={filterCountry} />

        <select style={{ ...inputBase, cursor: "pointer", flex: "0 1 140px" }} value={filterActive} onChange={(e) => setFilterActive(e.target.value)}>
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>

        {hasFilters && (
          <button onClick={() => { setSearch(""); setFilterCountry(""); setFilterState(""); setFilterActive(""); }}
            style={{ ...inputBase, background: "#fff5f5", color: "#dc2626", border: "1px solid #fecaca", cursor: "pointer", fontWeight: 600, fontSize: 13, whiteSpace: "nowrap" }}>
            Clear
          </button>
        )}
      </div>

      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr>{COLS.map((h) => <th key={h} style={{ padding: "11px 16px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {loading ? (
                <TableSkeleton />
              ) : allCities.length === 0 ? (
                <tr><td colSpan={COLS.length}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "56px 20px", gap: 8 }}>
                    <p style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#374151" }}>No cities found</p>
                    <p style={{ margin: 0, fontSize: 13, color: "#94a3b8" }}>{hasFilters ? "Try adjusting your filters" : "Add your first city to get started"}</p>
                    {!hasFilters && (
                      <button onClick={() => setShowAdd(true)} style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, padding: "8px 18px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                        <PlusIcon /> Add City
                      </button>
                    )}
                  </div>
                </td></tr>
              ) : (
                pagedCities.map((city, idx) => {
                  const isBusy = deletingId === city.id || activatingId === city.id;
                  return (
                    <tr key={city.id} className="cm-row"
                      style={{ borderBottom: "1px solid #f1f5f9", opacity: isBusy ? 0.35 : 1, pointerEvents: isBusy ? "none" : "auto", transition: "opacity 0.2s" }}>
                      <td style={{ padding: "13px 16px", color: "#94a3b8", fontSize: 13 }}>{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                      <td style={{ padding: "13px 16px", fontWeight: 600, color: "#0f172a" }}>{city.name}</td>
                      <td style={{ padding: "13px 16px", color: "#334155" }}>{city.state_name || "—"}</td>
                      <td style={{ padding: "13px 16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          {city.country_iso2 && <span style={{ fontSize: 11, fontWeight: 660, background: "#e0f2fe", color: "#0369a1", padding: "1px 5px", borderRadius: 4, letterSpacing: "0.04em" }}>{city.country_iso2}</span>}
                          <span style={{ color: "#334155" }}>{city.country_name || "—"}</span>
                        </div>
                      </td>
                      <td style={{ padding: "13px 16px", color: "#64748b", fontFamily: "monospace", fontSize: 13 }}>{city.latitude ?? "—"}</td>
                      <td style={{ padding: "13px 16px", color: "#64748b", fontFamily: "monospace", fontSize: 13 }}>{city.longitude ?? "—"}</td>
                      <td style={{ padding: "13px 16px" }}><Badge active={city.is_active} /></td>
                      <td style={{ padding: "13px 16px", color: "#94a3b8", fontSize: 12, whiteSpace: "nowrap" }}>{city.created_at || "—"}</td>
                      <td style={{ padding: "13px 16px" }}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button className="cm-edit-btn" title="Edit" onClick={() => openEdit(city)}
                            style={{ display: "flex", alignItems: "center", padding: "6px 10px", gap: 5, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 6, cursor: "pointer", color: "#3b82f6", fontSize: 12, fontWeight: 600, transition: "background 0.15s" }}>
                            <EditIcon /> Edit
                          </button>
                          {city.is_active ? (
                            <button className="cm-del-btn" title="Deactivate" onClick={() => setDeleteCity(city)}
                              style={{ display: "flex", alignItems: "center", padding: "6px 10px", gap: 5, background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: 6, cursor: "pointer", color: "#f43f5e", fontSize: 12, fontWeight: 600, transition: "background 0.15s" }}>
                              Deactivate
                            </button>
                          ) : (
                            // ── CHANGE 1: open confirm dialog instead of firing directly ──
                            <button className="cm-act-btn" title="Activate" onClick={() => setActivateCity(city)}
                              style={{ display: "flex", alignItems: "center", padding: "6px 10px", gap: 5, background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6, cursor: "pointer", color: "#16a34a", fontSize: 12, fontWeight: 600, transition: "background 0.15s" }}>
                              Activate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {!loading && allCities.length > 0 && (
          <Pagination currentPage={currentPage} totalPages={totalPages} totalCount={totalCount} onPageChange={(p) => { if (p >= 1 && p <= totalPages) setCurrentPage(p); }} />
        )}
      </div>

      {showAdd && (
        <Modal title="Add New City" onClose={() => { formDraft.clear(); setShowAdd(false); }}>
          <CityForm onSubmit={handleAdd} onCancel={() => { formDraft.clear(); setShowAdd(false); }} loading={actionLoading} />
        </Modal>
      )}

      {editCity && (
        <Modal title="Edit City" onClose={() => setEditCity(null)}>
          <CityForm initial={editCity} onSubmit={handleUpdate} onCancel={() => setEditCity(null)} loading={actionLoading} />
        </Modal>
      )}

      {deleteCity && (
        <Modal title="Deactivate City" onClose={() => setDeleteCity(null)}>
          <DeactivateConfirm city={deleteCity} onConfirm={handleDelete} onCancel={() => setDeleteCity(null)} loading={actionLoading} />
        </Modal>
      )}

      {/* ── CHANGE 1: Activate confirm dialog ── */}
      {activateCity && (
        <Modal title="Activate City" onClose={() => setActivateCity(null)}>
          <ActivateConfirm city={activateCity} onConfirm={handleActivateConfirm} onCancel={() => setActivateCity(null)} loading={!!activatingId} />
        </Modal>
      )}
    </div>
  );
};

export default CityMaster;